'use client'

import { motion } from 'framer-motion'
import { Space_Grotesk, Outfit } from 'next/font/google'
import { BackgroundBeams } from '../../_components/_ui/background-beams'
import { FaImage, FaLink, FaMarkdown, FaColumns, FaCheckSquare, FaCode, FaReceipt, FaShareAlt, FaCubes, FaAdversal, FaPlusCircle } from 'react-icons/fa'

const spaceGrotesk = Space_Grotesk({ 
  subsets: ['latin'],
  variable: '--font-space-grotesk',
  display: 'swap'
})

const outfit = Outfit({ 
  subsets: ['latin'],
  variable: '--font-outfit',
  display: 'swap'
})

const FeatureCard = ({ icon: Icon, title, description, colorClass, delay }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay }}
            className="bg-neutral-900/50 backdrop-blur-sm p-6 rounded-xl border border-neutral-800 hover:border-neutral-700 transition-all duration-300"
        >
            <div className={`flex items-center justify-center h-12 w-12 rounded-md ${colorClass} mb-4`}>
                <Icon className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-medium text-white mb-2">{title}</h3>
            <p className="text-gray-400">{description}</p>
        </motion.div>
    )
}

const Features = () => {
    const features = [
        {
            icon: FaImage,
            title: 'Image Blocks',
            description: 'Add single images or create beautiful galleries with rich previews and descriptions.',
            colorClass: 'bg-blue-500/10 text-blue-400'
        },
        {
            icon: FaLink,
            title: 'Link Cards',
            description: 'Create rich link previews that automatically pull metadata and create beautiful cards.',
            colorClass: 'bg-green-500/10 text-green-400'
        },
        {
            icon: FaMarkdown,
            title: 'Text Blocks',
            description: 'Add formatted text with markdown support for beautiful typography and structure.',
            colorClass: 'bg-purple-500/10 text-purple-400'
        },
        {
            icon: FaColumns,
            title: 'Kanban Boards',
            description: 'Organize tasks and content with drag-and-drop kanban boards.',
            colorClass: 'bg-yellow-500/10 text-yellow-400'
        },
        {
            icon: FaCheckSquare,
            title: 'Checklists',
            description: 'Create interactive checklists for guides, tutorials, and task lists.',
            colorClass: 'bg-indigo-500/10 text-indigo-400'
        },
        {
            icon: FaCode,
            title: 'Code Blocks',
            description: 'Share code with syntax highlighting and copy-to-clipboard functionality.',
            colorClass: 'bg-gray-500/10 text-gray-400'
        },
        {
            icon: FaReceipt,
            title: 'Record/Receipt',
            description: 'Track records, receipts, and expenses with custom fields and organization.',
            colorClass: 'bg-pink-500/10 text-pink-400'
        },
        {
            icon: FaShareAlt,
            title: 'Social Media',
            description: 'Add your social media profile links with beautiful previews.',
            colorClass: 'bg-cyan-500/10 text-cyan-400'
        },
        {
            icon: FaCubes,
            title: '3D Model Viewer',
            description: 'View 3D STL files interactively with rotation and zoom controls.',
            colorClass: 'bg-orange-500/10 text-orange-400'
        },
        {
            icon: FaAdversal,
            title: 'Ad Blocks',
            description: 'Monetize your page with customizable ad placements (Coming Soon).',
            colorClass: 'bg-red-500/10 text-red-400'
        },
        {
            icon: FaPlusCircle,
            title: 'And More',
            description: 'We are constantly adding new block types and features to help you share your content.',
            colorClass: 'bg-purple-500/10 text-purple-400'
        }
    ]

    return (
        <div className="min-h-screen w-full relative">
            <BackgroundBeams />
            <div className="relative z-10">
                {/* Hero Section */}
                <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mx-auto text-center">
                        <motion.h1 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="font-space-grotesk text-4xl sm:text-5xl font-bold text-white leading-tight mb-6"
                        >
                            Features
                        </motion.h1>
                        <motion.p 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="text-xl text-gray-300"
                        >
                            Everything you need to create beautiful, organized content pages.
                        </motion.p>
                    </div>
                </div>

                {/* Features Grid */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((feature, index) => (
                            <FeatureCard
                                key={feature.title}
                                icon={feature.icon}
                                title={feature.title}
                                description={feature.description}
                                colorClass={feature.colorClass}
                                delay={index * 0.1}
                            />
                        ))}
                    </div>
                </div>

                {/* CTA Section */}
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                        className="bg-neutral-900/50 backdrop-blur-sm rounded-2xl p-8 border border-neutral-800 text-center"
                    >
                        <h2 className="text-2xl font-bold text-white mb-4">Ready to Get Started?</h2>
                        <p className="text-gray-300 mb-8">
                            Create your first page in minutes and start sharing your content with the world.
                        </p>
                        <a 
                            href="/sign-up" 
                            className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
                        >
                            Create Your First Page
                        </a>
                    </motion.div>
                </div>
            </div>
        </div>
    )
}

export default Features 